import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Collapse,
  LinearProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Link,
  alpha,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  ArrowBack,
  Sync,
  Refresh,
  Close,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  ExpandMore,
  Schedule,
  InfoOutlined,
  Circle,
  Timer,
  Inventory2,
  TrendingUp,
  BarChart,
  ErrorOutline,
  PlayArrow,
  ArrowDropDown,
  Cancel,
  Settings,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useUrlTab } from '../hooks/useUrlTab';
import { ShopifyPriceLedgerSection } from '../components/integrations/ShopifyPriceLedgerSection';
import { integrationApiService, IntegrationConfig } from '../services/integrationApi';
import { useListingMatchesToConfirm } from '../components/mapping/v2/useListingMatchesToConfirm';
import { useAuth } from '../contexts/AuthContext';
import IntegrationExternalLinks from '../components/IntegrationExternalLinks';
import IntegrationSettingsDrawer from '../components/IntegrationSettingsDrawer';
import WhatMotzuSyncsSection from '../components/integrations/WhatMotzuSyncsSection';
import IntegrationMappingPanel from '../components/IntegrationMappingPanel';
import BigCommerceCompareCard from '../components/BigCommerceCompareCard';
import BigCommerceCategoryOwnerCard from '../components/BigCommerceCategoryOwnerCard';
import IntegrationSetupWalkthrough from '../components/integrationSetup/IntegrationSetupWalkthrough';
import LightspeedCatalogMatch from '../components/lightspeed/LightspeedCatalogMatch';
import LightspeedMappingPanel from '../components/lightspeed/LightspeedMappingPanel';
import LightspeedProductFields from '../components/lightspeed/LightspeedProductFields';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { platformDisplayLabel, platformLogo } from '../utils/storePlatform';
import StorefrontPresetsCard from '../components/StorefrontPresetsCard';
import StorefrontPresetGallery from '../components/storefrontPresets/StorefrontPresetGallery';
import ReportSyncIssueButton from '../components/ReportSyncIssueButton';
import { SettingsAnchor, SETTINGS_ANCHOR_PARAM, settingsAnchorFromParam } from '../components/dataIngestion/skipReasonActions';
import { clampSkipCounts, groupSkipReasons, presentSyncFailure } from '../components/dataIngestion/syncRunPresentation';
import { syncIssueSourceKey } from '../components/syncIssueReport';
import { apiService } from '../services/api';
import {
  resolveDisplaySyncStatus,
  resolveSyncRunTrigger,
  syncRunDisplayValue,
  syncRunSortMs,
  syncTriggerHint,
  syncTriggerLabel,
} from '../utils/syncRunStatus';
import {
  shopifySyncRunExtras,
  resolveSyncRunKind,
  syncRunKindChipLabel,
  syncRunKindHint,
  syncRunKindLabel,
  syncRunKindTone,
  syncRunUpdatedNoun,
} from '../utils/syncRunKind';
import { RunDetailField, SyncRunTriggerBlock } from '../components/integrations/SyncRunDetailFields';
import { syncRunStarterLabel } from '../utils/syncRunStarter';
import { PageContainer, StatusChip } from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { tokens } from '../theme/tokens';
import { maskedSecret } from '../utils/integrationCredentials';
import { categorySlugs } from '../components/mapping/mappingModel';
import { DETAIL_TABS, legacySettingsTabRedirect, legacyShopifyMappingRedirect, mappingSectionFromUrl, mappingTabAttention, resolveDetailTab, shopifyMappingHref } from './integrationDetailTabs';
import { visuallyHiddenSx } from '../components/mapping/v2/v2Ui';
import type { MappingSection } from '../components/mapping/mappingSections';

/* ── helpers ────────────────────────────────────────────────── */

const parseFirestoreDate = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'object') {
    const rec = value as Record<string, any>;
    if (typeof rec.toDate === 'function') {
      const d = rec.toDate();
      if (d instanceof Date && !Number.isNaN(d.getTime())) return d;
    }
    const seconds = typeof rec.seconds === 'number' ? rec.seconds
      : typeof rec._seconds === 'number' ? rec._seconds
      : null;
    if (seconds !== null) {
      const d = new Date(seconds * 1000);
      if (!Number.isNaN(d.getTime())) return d;
    }
  }
  return null;
};

const fmt = (value: unknown, opts?: Intl.DateTimeFormatOptions): string => {
  const d = parseFirestoreDate(value);
  if (!d) return '\u2014';
  return d.toLocaleDateString('en-US', opts || { month: 'short', day: '2-digit', year: 'numeric' });
};

const fmtDT = (value: unknown): string => {
  const d = parseFirestoreDate(value);
  if (!d) return '\u2014';
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatSyncError = (raw: string): string => {
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null) {
      return Object.entries(parsed)
        .map(([key, msgs]) => {
          const msgStr = Array.isArray(msgs) ? msgs.join('; ') : String(msgs);
          return `${key}: ${msgStr}`;
        })
        .join(' | ');
    }
  } catch {
    // not JSON
  }
  return raw;
};

const fmtDuration = (ms: number) => {
  if (!ms || ms <= 0) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

const getElapsedMs = (run: any, nowMs: number): number => {
  const started = parseFirestoreDate(run.startedAt || run.queuedAt || run.createdAt);
  if (!started) return 0;
  return Math.max(0, nowMs - started.getTime());
};

/**
 * Linear ETA from average products/ms so far; null if not enough signal.
 * Avoid showing a time after 1–2 products — one slow outlier makes the estimate absurd (e.g. 18h).
 */
const estimateRemainingMs = (run: any, nowMs: number): number | null => {
  if (run.status !== 'running') return null;
  const total = Number(run.productsTotal) || 0;
  const rawDone = (Number(run.productsProcessed) || 0) + (Number(run.productsSkipped) || 0);
  const done = total > 0 ? Math.min(rawDone, total) : rawDone;
  if (total <= 0 || done < 1 || done >= total) return null;
  const minSamples = Math.min(15, Math.max(5, Math.ceil(total * 0.01)));
  if (done < minSamples) return null;
  const elapsed = getElapsedMs(run, nowMs);
  if (elapsed < 5000) return null;
  const rate = done / elapsed;
  if (rate <= 0) return null;
  const remaining = total - done;
  const eta = remaining / rate;
  const cap = 7 * 24 * 60 * 60 * 1000;
  return Math.min(eta, cap);
};

const fmtEtaRemaining = (ms: number): string => {
  if (!ms || ms <= 0) return '—';
  if (ms < 60000) return `${Math.max(1, Math.round(ms / 1000))}s`;
  if (ms < 3600000) return `${(ms / 60000).toFixed(1)}m`;
  return `${(ms / 3600000).toFixed(1)}h`;
};

/**
 * When a run shows 0/N products for a while, explain that work happens before the first batch completes.
 */
const getSyncZeroProgressExplanation = (run: any, nowMs: number): string | null => {
  const total = Number(run.productsTotal) || 0;
  if (total <= 0) return null;
  const done = (Number(run.productsProcessed) || 0) + (Number(run.productsSkipped) || 0);
  if (done > 0) return null;

  if (run.status === 'queued') {
    return (
      'This run is waiting for a background worker. Until it starts, progress stays at 0. The queue is checked about every minute.'
    );
  }
  if (run.status !== 'running') return null;

  const elapsed = getElapsedMs(run, nowMs);
  const tail =
    elapsed > 120000
      ? ' Large selections, API rate limits, or a cold start can add a few minutes before numbers move.'
      : '';
  const stallHint =
    elapsed > 15 * 60 * 1000
      ? ' If counters are still zero after ~15 minutes, cancel this run and try Sync again — the worker may need a restart or there may be an API issue (check server logs with this run ID).'
      : '';

  return (
    'The worker loads your selection, Shopify mappings, map pricing, and catalog rows, then pushes to Shopify in batches (up to 50 products per batch). Progress counters update several times per batch while products are processed, not only when a batch completes.' +
      tail +
      stallHint
  );
};

/** A completed run that created and updated nothing (every product skipped), so "Completed" is not read as "synced". */
const wroteNothing = (run: any): boolean =>
  run?.status === 'success' &&
  typeof run.productsCreated === 'number' &&
  typeof run.productsUpdated === 'number' &&
  run.productsCreated === 0 &&
  run.productsUpdated === 0 &&
  (run.productsProcessed ?? 0) + (run.productsSkipped ?? 0) > 0;

/** "Request sent" / "Lightspeed said" detail for a failed product (platform-neutral optional fields on the error row). */
const SyncErrorDetail: React.FC<{ requestBodyPreview?: string; providerMessage?: string; truncated?: boolean }> = ({ requestBodyPreview, providerMessage, truncated }) => {
  const [open, setOpen] = useState(false);
  if (!requestBodyPreview && !providerMessage && !truncated) return null;
  return (
    <Box sx={{ mt: 0.5 }}>
      <Button size="small" variant="text" onClick={() => setOpen((v) => !v)} sx={{ minWidth: 0, px: 0.5, fontSize: '0.75rem' }}>
        {open ? 'Hide request details' : 'Show request details'}
      </Button>
      <Collapse in={open} unmountOnExit>
        {providerMessage && (
          <>
            <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block' }}>Lightspeed said</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontFamily: tokens.font.mono, wordBreak: 'break-word' }}>{providerMessage}</Typography>
          </>
        )}
        {truncated && !requestBodyPreview && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Request body not stored: this run had too many failures. Re-run the products individually to see it.
          </Typography>
        )}
        {requestBodyPreview && (
          <>
            <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block' }}>Request sent</Typography>
            <Box component="pre" sx={{ m: 0, p: 1, maxHeight: 280, overflow: 'auto', fontSize: '0.7rem', fontFamily: tokens.font.mono, bgcolor: 'action.hover', borderRadius: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {requestBodyPreview}
            </Box>
          </>
        )}
      </Collapse>
    </Box>
  );
};

const statusCfg = (s: string, theme: Theme) => {
  switch (s) {
    case 'success': return { label: 'Completed', color: theme.palette.success.main, bg: alpha(theme.palette.success.main, 0.06), icon: <CheckCircle sx={{ fontSize: 15 }} /> };
    case 'error':     return { label: 'Failed',    color: theme.palette.error.main, bg: alpha(theme.palette.error.main, 0.06), icon: <ErrorIcon sx={{ fontSize: 15 }} /> };
    case 'cancelled': return { label: 'Cancelled', color: theme.palette.grey[500], bg: alpha(theme.palette.grey[500], 0.12), icon: <Cancel sx={{ fontSize: 15 }} /> };
    case 'partial':   return { label: 'Partial with errors', color: theme.palette.warning.main, bg: alpha(theme.palette.warning.main, 0.08), icon: <Warning sx={{ fontSize: 15 }} /> };
    case 'running': return { label: 'Running',   color: theme.palette.primary.main, bg: alpha(theme.palette.primary.main, 0.06), icon: <Sync sx={{ fontSize: 15, animation: 'spin 1.2s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} /> };
    case 'queued':  return { label: 'Queued',    color: theme.palette.info.main, bg: alpha(theme.palette.info.main, 0.08), icon: <Schedule sx={{ fontSize: 15 }} /> };
    default:        return { label: s || 'Unknown', color: theme.palette.grey[500], bg: alpha(theme.palette.grey[500], 0.06), icon: <Circle sx={{ fontSize: 10 }} /> };
  }
};

/* ── component ──────────────────────────────────────────────── */

const IntegrationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const { isAdmin } = useAuth();
  const [expandedRun, setExpandedRun] = useState<string | null>(null);
  const [tab] = useUrlTab(DETAIL_TABS, 'overview');
  // The Mapping tab's sections are links too (`?tab=mapping&section=name-rules`).
  const [searchParams, setSearchParams] = useSearchParams();
  const rawSection = searchParams.get('section');
  // A MOT-252 alias (`?tab=matching`) opens its section on this render; the URL is rewritten below.
  const mappingSection = mappingSectionFromUrl(tab, rawSection);
  const setMappingSection = (section: MappingSection) =>
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('section', section);
      return params;
    }, { replace: true });
  // Changing tab drops a section left over from Mapping, in one URL write.
  const openTab = (next: typeof DETAIL_TABS[number]) =>
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete('section');
      if (next === 'overview') params.delete('tab');
      else params.set('tab', next);
      return params;
    }, { replace: true });
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionTestMessage, setConnectionTestMessage] = useState<string | null>(null);
  const { showToast } = useToast();
  const { t } = useAppTranslation();
  const { enabled: setupWalkthroughEnabled } = useOrgFeatureFlag('integration_setup_walkthrough');
  const { enabled: priceLedgerEnabled } = useOrgFeatureFlag('shopify_price_ledger');
  const { enabled: lightspeedLinkOnlyEnabled } = useOrgFeatureFlag('lightspeed_link_only');
  const { enabled: lightspeedStoreConfigEnabled } = useOrgFeatureFlag('lightspeed_store_config');
  const [walkthroughOpen, setWalkthroughOpen] = useState(false);
  // sourceKeys reported in this session, merged with what the server already has.
  const [locallyReportedKeys, setLocallyReportedKeys] = useState<Set<string>>(new Set());

  const { data: integration, isLoading: integrationLoading } = useQuery({
    queryKey: ['integration', id],
    queryFn: () => integrationApiService.getIntegration(id!),
    enabled: !!id,
  });

  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['integrationHistory', id],
    queryFn: () => integrationApiService.getIntegrationHistory(id!),
    enabled: !!id,
    refetchInterval: (query: any) => {
      const detailed = query?.state?.data?.detailed || [];
      return detailed.some((r: any) => r.status === 'queued' || r.status === 'running') ? 5000 : false;
    },
  });

  const listingHolds = useListingMatchesToConfirm({ id: id ?? '', platform: integration?.platform ?? '' } as Pick<IntegrationConfig, 'id' | 'platform'>);

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['integrationHistory', id] });
    queryClient.invalidateQueries({ queryKey: ['integration', id] });
  };

  const { data: reportedIssueKeys } = useQuery({
    queryKey: ['reportedSyncIssueKeys'],
    queryFn: () => apiService.getReportedSyncIssueKeys(),
    staleTime: 60_000,
  });

  const isIssueReported = (issue: { productId: string; message: string; code?: string }) => {
    const key = syncIssueSourceKey(issue);
    return locallyReportedKeys.has(key) || (reportedIssueKeys || []).includes(key);
  };

  const handleIssueReported = (sourceKey: string, duplicate: boolean) => {
    setLocallyReportedKeys((prev) => new Set(prev).add(sourceKey));
    queryClient.invalidateQueries({ queryKey: ['reportedSyncIssueKeys'] });
    if (duplicate) {
      showToast('Already reported — the Motzu team is reviewing this one.', 'info');
    } else {
      showToast('Sent to the Motzu team. Thanks for flagging it!', 'success');
    }
  };

  // A refused sync (e.g. 409 "Sync for BigCommerce is not available yet") says why instead of failing silently.
  const showSyncError = (error: unknown) =>
    showToast(error instanceof Error && error.message ? error.message : 'Sync request failed', 'error');
  const syncMutation = useMutation({ mutationFn: () => integrationApiService.syncIntegration(id!), onSuccess: invalidateAll, onError: showSyncError });
  const fullSyncMutation = useMutation({ mutationFn: () => integrationApiService.syncIntegration(id!, undefined, { forceFullSync: true }), onSuccess: invalidateAll, onError: showSyncError });
  const dataOnlySyncMutation = useMutation({
    mutationFn: () => integrationApiService.syncIntegration(id!, undefined, { syncImages: false }),
    onSuccess: invalidateAll,
    onError: showSyncError,
  });
  const dataOnlyFullSyncMutation = useMutation({
    mutationFn: () => integrationApiService.syncIntegration(id!, undefined, { forceFullSync: true, syncImages: false }),
    onSuccess: invalidateAll,
    onError: showSyncError,
  });
  const compareSyncMutation = useMutation({ mutationFn: () => integrationApiService.syncIntegration(id!, undefined, { syncType: 'compare_update' }), onSuccess: invalidateAll, onError: showSyncError });
  const directSyncMutation = useMutation({ mutationFn: () => integrationApiService.syncIntegration(id!, 'direct'), onSuccess: invalidateAll, onError: showSyncError });
  const cancelSyncMutation = useMutation({ mutationFn: (syncId: string) => integrationApiService.cancelIntegrationSync(id!, syncId), onSuccess: invalidateAll });
  const [syncMenuAnchor, setSyncMenuAnchor] = useState<null | HTMLElement>(null);
  const anySyncPending =
    syncMutation.isPending ||
    fullSyncMutation.isPending ||
    dataOnlySyncMutation.isPending ||
    dataOnlyFullSyncMutation.isPending ||
    compareSyncMutation.isPending ||
    directSyncMutation.isPending;

  // Live elapsed time ticker for running syncs
  const [now, setNow] = useState(Date.now());
  const detailedRuns0: any[] = historyData?.detailed || [];
  const hasActiveRun = detailedRuns0.some((r: any) => r.status === 'queued' || r.status === 'running');
  useEffect(() => {
    if (!hasActiveRun) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [hasActiveRun]);

  // "Configure" links on skip reasons open the Sync Behavior tab, then scroll to
  // the setting once it has rendered.
  const [pendingSettingsAnchor, setPendingSettingsAnchor] = useState<SettingsAnchor | null>(null);
  useEffect(() => {
    if (!pendingSettingsAnchor || tab !== 'sync-behavior') return;
    const frame = requestAnimationFrame(() => {
      document.getElementById(pendingSettingsAnchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setPendingSettingsAnchor(null);
    });
    return () => cancelAnimationFrame(frame);
  }, [pendingSettingsAnchor, tab]);
  // A link from elsewhere (a scan Apply result) arrives as ?tab=sync-behavior&anchor=<setting>; scroll there once, then drop the parameter.
  const anchorParam = searchParams.get(SETTINGS_ANCHOR_PARAM);
  useEffect(() => {
    const anchor = settingsAnchorFromParam(anchorParam);
    if (!anchor) return;
    setPendingSettingsAnchor(anchor);
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete(SETTINGS_ANCHOR_PARAM);
      return params;
    }, { replace: true });
  }, [anchorParam, setSearchParams]);
  const openSettingsAt = (anchor: SettingsAnchor) => {
    setPendingSettingsAnchor(anchor);
    openTab('sync-behavior');
  };

  // A tab this integration does not have renders Overview, as before. The
  // MOT-252 Shopify tabs (Classification, Catalog Matching, Metafields; in
  // notifications, emails, bookmarks and support replies) are also rewritten
  // in the URL to their Mapping section. Only those are rewritten: the other
  // fallbacks depend on org flags that may still be loading.
  const isShopifyIntegration = integration?.platform === 'shopify';
  const visibleTab = integration
    ? resolveDetailTab(tab, {
        isShopify: isShopifyIntegration,
        showLightspeedConfig: integration.platform === 'lightspeed' && lightspeedStoreConfigEnabled,
        showCatalogMatch: integration.platform === 'lightspeed' && lightspeedLinkOnlyEnabled,
      })
    : tab;
  // `?tab=settings` (old Settings tab links) is rewritten to `?tab=sync-behavior` on every platform.
  useEffect(() => {
    const next = legacySettingsTabRedirect(tab);
    if (!next) return;
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('tab', next);
      return params;
    }, { replace: true });
  }, [tab, setSearchParams]);
  useEffect(() => {
    if (!isShopifyIntegration) return;
    const redirect = legacyShopifyMappingRedirect(tab, rawSection);
    if (!redirect) return;
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('tab', redirect.tab);
      if (redirect.section) params.set('section', redirect.section);
      else params.delete('section');
      return params;
    }, { replace: true });
  }, [isShopifyIntegration, tab, rawSection, setSearchParams]);

  if (integrationLoading || historyLoading) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box>;
  }
  if (!integration) {
    return <Alert severity="error" sx={{ m: 3 }}>Integration not found</Alert>;
  }

  const history = historyData?.summary;
  const detailedRuns: any[] = [...(historyData?.detailed || [])].sort(
    (a: any, b: any) => syncRunSortMs(b, parseFirestoreDate) - syncRunSortMs(a, parseFirestoreDate),
  );
  const latestRun = detailedRuns[0];
  const lastSyncValue = latestRun ? syncRunDisplayValue(latestRun) : integration.lastSync;
  const activeRun = detailedRuns.find((r: any) => r.status === 'queued' || r.status === 'running');
  const configuredInterval = integration.settings?.syncInterval;

  const isShopify = integration.platform === 'shopify';
  const showCatalogMatch = integration.platform === 'lightspeed' && lightspeedLinkOnlyEnabled;
  const showLightspeedConfig = integration.platform === 'lightspeed' && lightspeedStoreConfigEnabled;
  const permissionsWarning = isShopify && integration.scopeStatus?.needsReauth === true;
  const shortId = String(
    integration.credentials?.shopId
    || integration.credentials?.accountId
    || integration.credentials?.storeHash
    || '',
  );
  const platformLabel = platformDisplayLabel(integration.platform, t);
  // The latest run stopped on something only the store can fix (e.g. a read-only API key, MOT-796).
  const needsAttention = Boolean(integration.lastSyncSummary?.runErrorCode);
  const statusLabel = !integration.isActive ? 'Inactive' : needsAttention ? 'Needs attention' : lastSyncValue ? 'Synced' : 'Not synced';
  const statusColor = !integration.isActive
    ? tokens.ink.faint
    : needsAttention
      ? tokens.signal.problem.solid
      : lastSyncValue
        ? tokens.signal.positive.solid
        : tokens.signal.caution.solid;
  const issueRuns = (history?.failedRuns ?? 0) + (history?.partialRuns ?? 0);
  const lookName = integration.settings?.appliedStorefrontPreset?.presetName;
  const logo = platformLogo(integration.platform);

  // Write-only credentials arrive as `<field>Set` / `<field>Last4`; the value never reaches the browser.
  const storedCredentials = (integration.credentials || {}) as Record<string, unknown>;
  const maskSecret = (field: string) => maskedSecret(storedCredentials, field, t('integrations.savedCredentials.saved')) || '—';
  const isCredentialMarker = (key: string) =>
    (key.endsWith('Set') && typeof storedCredentials[key] === 'boolean') || key.endsWith('Last4');
  const secretCredentialRows = Object.keys(storedCredentials)
    .filter((key) => key.endsWith('Set') && typeof storedCredentials[key] === 'boolean')
    .map((key) => key.slice(0, -'Set'.length))
    .map((field) => [field, maskSecret(field)]);

  // Same query key as the Mapping tab's listing-match list, so confirming a hold clears the badge.
  const listingHoldCount = listingHolds.count;

  // Motzu categories with no Shopify type in the saved map: they use the fallback (the Mapping tab's dot).
  // Same count in both layouts, and the same one the overrides menu line shows.
  const storeLayout = integration.settings?.storeLayout;
  const unmappedCategoryCount = isShopify
    ? categorySlugs(storeLayout).filter((slug) => !storeLayout?.categoryMap?.[slug]?.trim()).length
    : 0;

  type DetailTabItem = {
    id: typeof DETAIL_TABS[number];
    label: string;
    dot?: boolean;
    badge?: number;
    badgeTone?: 'caution';
    /** Read to screen readers in place of the dot and badge, which say it by colour and number only. */
    srText?: string;
  };
  const historyTab: DetailTabItem = { id: 'history', label: 'Sync History', badge: issueRuns > 0 ? issueRuns : undefined };
  // Shopify (MOT-278): one Mapping tab whose section menu holds Classification, Metafields and Catalog
  // Matching. Its badge counts the listing matches waiting to be confirmed; a dot says categories fall back.
  const detailTabs: DetailTabItem[] = isShopify
    ? [
        { id: 'overview', label: 'Overview' },
        {
          id: 'mapping',
          label: t('integrations.shopifyDetail.tabs.mapping'),
          dot: unmappedCategoryCount > 0,
          ...(listingHoldCount > 0 ? { badge: listingHoldCount, badgeTone: 'caution' as const } : {}),
          srText: mappingTabAttention(t, { listingMatches: listingHoldCount, unmatchedCategories: unmappedCategoryCount }),
        },
        { id: 'fields', label: 'Product Fields' },
        { id: 'storefront', label: 'Storefront Look', dot: !lookName },
        { id: 'sync-behavior', label: t('integrations.detailTabs.syncBehavior') },
        historyTab,
        { id: 'credentials', label: t('integrations.shopifyDetail.tabs.connection'), dot: permissionsWarning },
      ]
    : [
        { id: 'overview', label: 'Overview' },
        { id: 'credentials', label: 'Credentials' },
        ...(showLightspeedConfig ? [
          { id: 'fields' as const, label: t('integrations.lightspeedConfig.tabs.fields') },
          { id: 'mapping' as const, label: t('integrations.lightspeedConfig.tabs.mapping') },
        ] : []),
        ...(showCatalogMatch ? [{ id: 'catalog' as const, label: t('integrations.lightspeedCatalogMatch.title') }] : []),
        historyTab,
        { id: 'sync-behavior', label: t('integrations.detailTabs.syncBehavior') },
      ];

  return (
    <PageContainer maxWidth={1280}>

      <Button
        onClick={() => navigate('/app/integrations')}
        startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
        sx={{ alignSelf: 'flex-start', px: 0, minWidth: 0, color: tokens.ink.bodyAlt, fontWeight: 600 }}
      >
        Integrations
      </Button>

      <Box
        sx={{
          bgcolor: tokens.surface.card,
          border: '1px solid',
          borderColor: tokens.line.default,
          borderRadius: `${tokens.radius.panel}px`,
          boxShadow: tokens.shadow.card,
          px: 2.5,
          py: 2.25,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ width: 44, height: 44, borderRadius: '11px', bgcolor: tokens.surface.sunken, display: 'grid', placeItems: 'center', flex: '0 0 auto' }}>
          <Box component="img" src={logo} alt="" sx={{ maxHeight: 28, maxWidth: 36, objectFit: 'contain' }} />
        </Box>
        <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 700, color: tokens.ink.primary, overflowWrap: 'anywhere' }}>
            {integration.name}{' '}
            {shortId && (
              <Box component="span" sx={{ fontSize: 12, fontWeight: 500, color: tokens.ink.faint }}>{shortId}</Box>
            )}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            {platformLabel}
            <span>·</span>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: statusColor, fontWeight: 600 }}>
              <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: statusColor }} />
              {statusLabel}
            </Box>
            <span>·</span>
            Last sync {lastSyncValue ? fmt(lastSyncValue) : 'never'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, flex: '0 1 auto', minWidth: 0, flexWrap: 'wrap', '& .MuiButton-root': { whiteSpace: 'nowrap' } }}>
          <IntegrationExternalLinks integration={integration} />
          {setupWalkthroughEnabled && !integration.setupWalkthrough?.completedAt && (
            <Button variant="outlined" onClick={() => setWalkthroughOpen(true)} sx={{ height: 36 }}>
              {t('integrations.setupWalkthrough.finishSetup')}
            </Button>
          )}
          <Box>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.ink.faint }}>
              Created
            </Typography>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: tokens.ink.body }}>{fmt(integration.createdAt)}</Typography>
          </Box>
          <Box sx={{ width: '1px', height: 26, bgcolor: tokens.line.default, display: { xs: 'none', sm: 'block' } }} />
          <Box sx={{ display: 'flex' }}>
            <Button
              variant="contained"
              onClick={() => syncMutation.mutate()}
              disabled={anySyncPending}
              startIcon={anySyncPending ? <CircularProgress size={14} color="inherit" /> : <Sync sx={{ fontSize: 16 }} />}
              sx={{ height: 36, borderRadius: '9px 0 0 9px' }}
            >
              {anySyncPending ? 'Syncing…' : 'Sync Now'}
            </Button>
            <Button
              variant="contained"
              aria-label="More sync options"
              onClick={(e) => setSyncMenuAnchor(e.currentTarget)}
              disabled={anySyncPending}
              sx={{ height: 36, minWidth: 30, px: 0.5, borderRadius: '0 9px 9px 0', borderLeft: '1px solid', borderLeftColor: 'divider' }}
            >
              <ArrowDropDown sx={{ fontSize: 18 }} />
            </Button>
            <Menu
              anchorEl={syncMenuAnchor}
              open={Boolean(syncMenuAnchor)}
              onClose={() => setSyncMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={() => { setSyncMenuAnchor(null); syncMutation.mutate(); }}>
                <ListItemIcon><Sync fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary="Sync changes"
                  secondary="Changed products only · images follow Sync Behavior"
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                  secondaryTypographyProps={{ variant: 'caption', sx: { whiteSpace: 'normal', lineHeight: 1.35, display: 'block', mt: 0.25 } }}
                />
              </MenuItem>
              <MenuItem onClick={() => { setSyncMenuAnchor(null); fullSyncMutation.mutate(); }}>
                <ListItemIcon><Sync fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText
                  primary="Force full sync"
                  secondary="All selected products (even if unchanged) · images follow Sync Behavior"
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                  secondaryTypographyProps={{ variant: 'caption', sx: { whiteSpace: 'normal', lineHeight: 1.35, display: 'block', mt: 0.25 } }}
                />
              </MenuItem>
              <Divider component="li" sx={{ my: 0.5, opacity: 0.6 }} />
              <MenuItem onClick={() => { setSyncMenuAnchor(null); dataOnlySyncMutation.mutate(); }}>
                <ListItemIcon><Sync fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText
                  primary="Sync changes (no images)"
                  secondary="Changed products only · does not send images"
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                  secondaryTypographyProps={{ variant: 'caption', sx: { whiteSpace: 'normal', lineHeight: 1.35, display: 'block', mt: 0.25 } }}
                />
              </MenuItem>
              <MenuItem onClick={() => { setSyncMenuAnchor(null); dataOnlyFullSyncMutation.mutate(); }}>
                <ListItemIcon><Sync fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText
                  primary="Force full sync (no images)"
                  secondary="All selected products (even if unchanged) · does not send images"
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                  secondaryTypographyProps={{ variant: 'caption', sx: { whiteSpace: 'normal', lineHeight: 1.35, display: 'block', mt: 0.25 } }}
                />
              </MenuItem>
              <Divider component="li" sx={{ my: 0.5, opacity: 0.6 }} />
              <MenuItem onClick={() => { setSyncMenuAnchor(null); compareSyncMutation.mutate(); }}>
                <ListItemIcon><Sync sx={{ fontSize: 18 }} /></ListItemIcon>
                <ListItemText
                  primary="Compare & update"
                  secondary="Reads the store first, then updates only where Motzu data differs"
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                  secondaryTypographyProps={{ variant: 'caption', sx: { whiteSpace: 'normal', lineHeight: 1.35, display: 'block', mt: 0.25 } }}
                />
              </MenuItem>
            </Menu>
          </Box>
          <IconButton
            aria-label={t('integrations.detailTabs.syncBehavior')}
            onClick={() => openTab('sync-behavior')}
            sx={{ width: 36, height: 36, border: '1px solid', borderColor: tokens.line.default, borderRadius: '9px' }}
          >
            <Settings sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, borderBottom: '1px solid', borderColor: tokens.line.default, overflowX: 'auto' }}>
        {detailTabs.map((item) => {
          const selected = visibleTab === item.id;
          return (
            <Button
              key={item.id}
              onClick={() => openTab(item.id)}
              sx={{
                height: 40,
                px: 1.75,
                borderRadius: 0,
                borderBottom: '2px solid',
                borderColor: selected ? tokens.accent.main : 'transparent',
                color: selected ? tokens.ink.primary : tokens.ink.muted,
                fontWeight: selected ? 700 : 500,
                fontSize: 13.5,
                minWidth: 0,
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
            >
              {item.label}
              {item.dot && (
                <Box component="span" sx={{ ml: 0.75, width: 6, height: 6, borderRadius: '50%', bgcolor: tokens.signal.caution.solid }} />
              )}
              {item.srText && <Box component="span" sx={visuallyHiddenSx}>{item.srText}</Box>}
              {item.badge ? (
                <Box
                  component="span"
                  aria-hidden={item.srText ? true : undefined}
                  sx={{
                    ml: 0.75,
                    minWidth: 18,
                    height: 18,
                    px: 0.5,
                    borderRadius: 9,
                    bgcolor: item.badgeTone === 'caution'
                      ? tokens.signal.caution.tint
                      : selected ? tokens.accent.tint : tokens.surface.tint,
                    color: item.badgeTone === 'caution'
                      ? tokens.signal.caution.ink
                      : selected ? tokens.accent.hover : tokens.ink.faint,
                    fontSize: 11,
                    fontWeight: 700,
                    lineHeight: '18px',
                  }}
                >
                  {item.badge}
                </Box>
              ) : null}
            </Button>
          );
        })}
      </Box>

      {visibleTab === 'overview' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fit, minmax(220px, 1fr))' }, gap: 1.5 }}>
            <Box
              component="button"
              type="button"
              onClick={() => openTab(permissionsWarning ? 'sync-behavior' : 'credentials')}
              sx={{ textAlign: 'left', display: 'flex', gap: 1.5, p: 1.75, bgcolor: tokens.surface.card, border: '1px solid', borderColor: tokens.line.default, borderRadius: `${tokens.radius.card}px`, cursor: 'pointer' }}
            >
              <Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>Connection</Typography>
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: permissionsWarning ? tokens.signal.caution.ink : integration.isActive ? tokens.signal.positive.ink : tokens.ink.muted }}>
                  {permissionsWarning
                    ? 'Reconnect recommended'
                    : integration.isActive
                      ? 'Active, permissions current'
                      : 'Inactive'}
                </Typography>
              </Box>
            </Box>
            {isShopify && (
              <Box
                component="button"
                type="button"
                onClick={() => openTab('storefront')}
                sx={{ textAlign: 'left', display: 'flex', gap: 1.5, p: 1.75, bgcolor: tokens.surface.card, border: '1px solid', borderColor: tokens.line.default, borderRadius: `${tokens.radius.card}px`, cursor: 'pointer' }}
              >
                <Box>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>Storefront look</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: lookName ? tokens.signal.positive.ink : tokens.signal.caution.ink }}>
                    {lookName || 'No look chosen yet'}
                  </Typography>
                </Box>
              </Box>
            )}
            {isShopify && (
              <Box
                component="button"
                type="button"
                onClick={() => openTab('fields')}
                sx={{ textAlign: 'left', display: 'flex', gap: 1.5, p: 1.75, bgcolor: tokens.surface.card, border: '1px solid', borderColor: tokens.line.default, borderRadius: `${tokens.radius.card}px`, cursor: 'pointer' }}
              >
                <Box>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>Product page fields</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: tokens.ink.muted }}>
                    Spec fields and theme blocks
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {history && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fit, minmax(140px, 1fr))' }, gap: 1.25 }}>
              {[
                ['Total runs', history.totalRuns ?? 0],
                ['Successful', history.successfulRuns ?? 0],
                ['Partial', history.partialRuns ?? 0],
                ['Failed', history.failedRuns ?? 0],
                ['Products in selection', history.selectionProductCount ?? history.totalProductsSynced ?? 0],
              ].map(([label, value]) => (
                <Box key={String(label)} sx={{ display: 'flex', flexDirection: 'column', p: 1.5, bgcolor: tokens.surface.card, border: '1px solid', borderColor: tokens.line.default, borderRadius: `${tokens.radius.card}px` }}>
                  <Typography sx={{ fontSize: 19, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{Number(value).toLocaleString()}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: tokens.ink.muted }}>{label}</Typography>
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1.6fr) minmax(260px,1fr)' }, gap: 2 }}>
            <Box sx={{ bgcolor: tokens.surface.card, border: '1px solid', borderColor: tokens.line.default, borderRadius: `${tokens.radius.panel}px`, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Recent Activity</Typography>
                <Button size="small" onClick={() => openTab('history')} sx={{ ml: 'auto', fontWeight: 600 }}>
                  View full sync history
                </Button>
              </Box>
              {detailedRuns.slice(0, 4).map((run) => {
                const issues = (Number(run.warningCount) || 0) + (Number(run.errorCount) || 0) + (Array.isArray(run.errors) ? run.errors.length : 0);
                const done = (Number(run.productsProcessed) || 0);
                const total = Number(run.productsTotal) || done;
                return (
                  <Box key={run.id || run.startedAt} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderTop: '1px solid', borderColor: tokens.line.hairline }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: issues > 0 ? tokens.signal.caution.solid : tokens.signal.positive.solid, flex: '0 0 auto' }} />
                    <Typography sx={{ width: 150, flex: '0 0 auto', fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>{fmtDT(run.completedAt || run.startedAt)}</Typography>
                    <Typography sx={{ flex: 1, minWidth: 0, fontSize: 12.5, color: tokens.ink.muted }} noWrap>
                      {done}/{total} products · {Number(run.productsUpdated) || 0} updated · {issues} issues
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {fmtDuration(Number(run.duration) || 0)}
                    </Typography>
                  </Box>
                );
              })}
              {detailedRuns.length === 0 && (
                <Typography sx={{ fontSize: 13, color: tokens.ink.muted, py: 2 }}>No sync runs yet.</Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {permissionsWarning && (
                <Box sx={{ bgcolor: tokens.accent.tint, border: '1px solid', borderColor: tokens.accent.tintStrong, borderRadius: `${tokens.radius.card}px`, p: 1.75 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: tokens.accent.hover }}>Permissions changed</Typography>
                  <Typography sx={{ fontSize: 12.5, color: tokens.accent.hover, mt: 0.5 }}>
                    Motzu has added permissions since this store connected. Check whether it needs to reconnect.
                  </Typography>
                  <Button size="small" onClick={() => openTab('sync-behavior')} sx={{ mt: 1, fontWeight: 700, px: 0 }}>
                    Check permissions
                  </Button>
                </Box>
              )}
              {integration.platform === 'bigcommerce' && (
                <BigCommerceCompareCard integration={integration} onChanged={invalidateAll} />
              )}
              {integration.platform === 'bigcommerce' && (
                <BigCommerceCategoryOwnerCard integration={integration} onChanged={invalidateAll} />
              )}
              {isShopify && (
                <Box sx={{ bgcolor: tokens.surface.card, border: '1px solid', borderColor: tokens.line.default, borderRadius: `${tokens.radius.card}px`, p: 1.75 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: tokens.signal.positive.ink }}>Duplicate mapping cleanup</Typography>
                  <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, mt: 0.5 }}>
                    Remove extra Motzu mapping rows referencing duplicate Shopify listings.
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(`/app/integrations/${id}/shopify-duplicates`)}
                    sx={{ mt: 1.25, color: tokens.signal.positive.ink, borderColor: tokens.signal.positive.border }}
                  >
                    Run cleanup
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {visibleTab === 'credentials' && (
        <Box sx={{ bgcolor: tokens.surface.card, border: '1px solid', borderColor: tokens.line.default, borderRadius: `${tokens.radius.panel}px`, overflow: 'hidden' }}>
          <Box sx={{ px: 2.25, py: 1.5, bgcolor: tokens.surface.sunken, borderBottom: '1px solid', borderColor: tokens.line.default, display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.ink.muted }}>
              Connection details
            </Typography>
            <Button size="small" sx={{ ml: 'auto', fontWeight: 700 }} onClick={() => navigate(`/app/integrations?configure=${id}&tab=credentials`)}>
              Edit
            </Button>
          </Box>
          <Box sx={{ px: 2.25, py: 1 }}>
            {(isShopify
              ? [
                  ['Shop ID', shortId || '—'],
                  ['Shop Domain', String(integration.credentials?.shopDomain || (shortId ? `${shortId}.myshopify.com` : '—'))],
                  ['Admin API Token', maskSecret('accessToken')],
                  ['API Version', '2026-04'],
                ]
              : integration.platform === 'lightspeed'
                ? [
                    ['Account ID', String(integration.credentials?.accountId || '—')],
                    ['Access Token', maskSecret('accessToken')],
                    ['Refresh Token', maskSecret('refreshToken')],
                  ]
                : [
                    ...Object.entries(storedCredentials)
                      .filter(([key]) => !['installMethod', 'customApp', 'grantedScopes'].includes(key) && !isCredentialMarker(key))
                      .map(([key, value]) => [key, String(value || '—')]),
                    ...secretCredentialRows,
                  ].slice(0, 8)
            ).map(([label, value]) => (
              <Box key={String(label)} sx={{ display: 'flex', gap: 2, py: 1.25, borderTop: '1px solid', borderColor: tokens.line.hairline, '&:first-of-type': { borderTop: 'none' } }}>
                <Typography sx={{ width: 160, flex: '0 0 auto', fontSize: 13, color: tokens.ink.muted }}>{label}</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{value}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ px: 2.25, pb: 2 }}>
            <Button
              variant="outlined"
              disabled={testingConnection}
              onClick={async () => {
                setTestingConnection(true);
                setConnectionTestMessage(null);
                try {
                  const result = await integrationApiService.testIntegration(id!);
                  setConnectionTestMessage(result?.success === true ? 'Connection succeeded' : (result?.message || 'Connection failed'));
                } catch (err) {
                  setConnectionTestMessage(err instanceof Error ? err.message : 'Connection failed');
                } finally {
                  setTestingConnection(false);
                }
              }}
              sx={{ bgcolor: tokens.accent.tint, borderColor: tokens.accent.tintStrong, color: tokens.accent.hover }}
            >
              {testingConnection ? 'Testing…' : 'Test Connection'}
            </Button>
            {connectionTestMessage && (
              <Typography sx={{ mt: 1, fontSize: 13, color: tokens.ink.muted }}>{connectionTestMessage}</Typography>
            )}
          </Box>
        </Box>
      )}

      {visibleTab === 'storefront' && isShopify && id && (
        <StorefrontPresetGallery
          integrationId={id}
          storeName={integration.name}
          appliedPreset={integration.settings.appliedStorefrontPreset}
          onApplied={() => {
            queryClient.invalidateQueries({ queryKey: ['integration', id] });
            queryClient.invalidateQueries({ queryKey: ['integrations'] });
          }}
        />
      )}

      {visibleTab === 'fields' && isShopify && id && (
        <StorefrontPresetsCard
          integrationId={id}
          lastSync={integration.lastSync}
          motzuMetafields={integration.settings?.motzuMetafields}
          customApp={integration.credentials?.customApp === true}
        />
      )}

      {/* Every Mapping section is a panel of one editor, so unsaved edits and the one save bar survive moving between them. */}
      {isShopify && visibleTab === 'mapping' && (
        <IntegrationMappingPanel
          integration={integration}
          onOpenProductFields={() => openTab('fields')}
          section={mappingSection}
          onSection={setMappingSection}
        />
      )}

      {visibleTab === 'fields' && showLightspeedConfig && id && (
        <LightspeedProductFields integrationId={id} onOpenMapping={() => openTab('mapping')} />
      )}

      {visibleTab === 'mapping' && showLightspeedConfig && (
        <LightspeedMappingPanel integration={integration} onOpenProductFields={() => openTab('fields')} />
      )}

      {visibleTab === 'catalog' && showCatalogMatch && id && <LightspeedCatalogMatch integrationId={id} />}

      {visibleTab === 'sync-behavior' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <IntegrationSettingsDrawer
            variant="page"
            open
            onClose={() => openTab('overview')}
            integration={integration}
            onRunSetupAgain={
              setupWalkthroughEnabled && integration.setupWalkthrough?.completedAt
                ? () => setWalkthroughOpen(true)
                : undefined
            }
          />
          <WhatMotzuSyncsSection integration={integration} />
          {isShopify ? (
            <Button variant="text" onClick={() => openTab('mapping')} sx={{ alignSelf: 'flex-start', fontWeight: 600 }}>
              {t('integrations.shopifyDetail.openMapping')}
            </Button>
          ) : showLightspeedConfig ? (
            <Button variant="text" onClick={() => openTab('mapping')} sx={{ alignSelf: 'flex-start', fontWeight: 600 }}>
              {t('integrations.lightspeedConfig.openMapping')}
            </Button>
          ) : showCatalogMatch ? (
            <Button variant="text" onClick={() => openTab('catalog')} sx={{ alignSelf: 'flex-start', fontWeight: 600 }}>
              {t('integrations.lightspeedCatalogMatch.openTab')}
            </Button>
          ) : (
            <Button variant="text" onClick={() => navigate(`/app/integrations?configure=${id}`)} sx={{ alignSelf: 'flex-start', fontWeight: 600 }}>
              Store layout and product matching
            </Button>
          )}
        </Box>
      )}

      {setupWalkthroughEnabled && (
        <IntegrationSetupWalkthrough
          open={walkthroughOpen}
          integration={integration}
          onClose={() => setWalkthroughOpen(false)}
          onFinished={() => {
            setWalkthroughOpen(false);
            showToast(t('integrations.setupWalkthrough.finished'), 'success');
          }}
        />
      )}

      {/* ── Active run banner ── */}
      {activeRun && (() => {
        const cfg = statusCfg(resolveDisplaySyncStatus(activeRun), theme);
        return (
          <Paper
            variant="outlined"
            sx={{ mb: 3, p: 2, borderColor: alpha(cfg.color, 0.3), bgcolor: cfg.bg }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.35,
                borderRadius: 1, bgcolor: alpha(cfg.color, 0.12), color: cfg.color, fontWeight: 600, fontSize: '0.75rem',
              }}>
                {cfg.icon}
                {cfg.label}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                {activeRun.status === 'queued'
                  ? `Sync queued · ${activeRun.productsTotal ? `${activeRun.productsTotal} products in this run · ` : ''}worker checks about every minute. This page auto-refreshes.`
                  : (() => {
                      const rawDone = (activeRun.productsProcessed ?? 0) + (activeRun.productsSkipped ?? 0);
                      const total = activeRun.productsTotal ?? 0;
                      const done = total > 0 ? Math.min(rawDone, total) : rawDone;
                      const elapsed = fmtDuration(getElapsedMs(activeRun, now));
                      const etaMs = estimateRemainingMs(activeRun, now);
                      const etaPart =
                        etaMs !== null ? ` · ~${fmtEtaRemaining(etaMs)} remaining` : '';
                      if (total > 0 && done === 0) {
                        return `Starting first batch: 0 / ${total} products counted yet · ${elapsed}${etaPart}`;
                      }
                      if (total > 0) {
                        const pct = Math.min(Math.round((done / total) * 100), 100);
                        return `Syncing: ${done} of ${total} products (${pct}%) · ${elapsed}${etaPart}`;
                      }
                      return `Sync is running… ${elapsed}${etaPart}`;
                    })()}
                {activeRun.id && <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 1, fontFamily: tokens.font.mono }}>(Run ID: {activeRun.id.slice(0, 12)}...)</Typography>}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                {isAdmin && (
                <Button size="small" variant="outlined" color="warning" onClick={() => directSyncMutation.mutate()} disabled={directSyncMutation.isPending || syncMutation.isPending}
                  sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                  {directSyncMutation.isPending ? 'Running...' : 'Run Direct Fallback'}
                </Button>
                )}
                <Button size="small" variant="outlined" color="error" onClick={() => cancelSyncMutation.mutate(activeRun.id)} disabled={cancelSyncMutation.isPending}
                  sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                  {cancelSyncMutation.isPending ? 'Cancelling...' : 'Cancel This Sync'}
                </Button>
              </Box>
            </Box>
            {getSyncZeroProgressExplanation(activeRun, now) && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25, lineHeight: 1.5, maxWidth: 900 }}>
                {getSyncZeroProgressExplanation(activeRun, now)}
              </Typography>
            )}
            {(() => {
              const total = activeRun.productsTotal ?? 0;
              const done = (activeRun.productsProcessed ?? 0) + (activeRun.productsSkipped ?? 0);
              const noProgressYet =
                total > 0 &&
                done === 0 &&
                (activeRun.status === 'running' || activeRun.status === 'queued');
              return (
                <LinearProgress
                  variant={total > 0 && !noProgressYet ? 'determinate' : 'indeterminate'}
                  value={
                    total > 0 && !noProgressYet
                      ? Math.min(Math.round((Math.min(done, total) / total) * 100), 100)
                      : undefined
                  }
                  sx={{ mt: 1.5, mx: -2, mb: -2, borderRadius: '0 0 8px 8px', height: 3 }}
                  color={activeRun.status === 'running' ? 'primary' : 'inherit'}
                />
              );
            })()}
          </Paper>
        );
      })()}

      {visibleTab === 'history' && (<>
      {/* ── Stats cards ── */}
      {history && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', md: 'repeat(6,1fr)' }, gap: 2, mb: 3 }}>
          {([
            { label: 'Total Runs', value: history.totalRuns, color: theme.palette.info.main, icon: <BarChart /> },
            { label: 'Successful', value: history.successfulRuns, color: theme.palette.success.main, icon: <CheckCircle /> },
            { label: 'Partial', value: history.partialRuns ?? 0, color: theme.palette.warning.main, icon: <Warning /> },
            { label: 'Failed', value: history.failedRuns, color: theme.palette.error.main, icon: <ErrorOutline /> },
            { label: 'Cancelled', value: history.cancelledRuns ?? 0, color: theme.palette.grey[500], icon: <Cancel /> },
            {
              label: 'Products in selection',
              value: (history.selectionProductCount ?? history.totalProductsSynced ?? 0).toLocaleString(),
              color: theme.palette.primary.main,
              icon: <Inventory2 />,
            },
          ] as const).map((stat) => (
            <Paper
              key={stat.label}
              variant="outlined"
              sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}
            >
              <Box sx={{
                width: 42, height: 42, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: alpha(stat.color, 0.08), color: stat.color, flexShrink: 0,
                '& .MuiSvgIcon-root': { fontSize: 22 },
              }}>
                {stat.icon}
              </Box>
              <Box>
                <Typography variant="h5" sx={{ lineHeight: 1.2 }}>{stat.value}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>{stat.label}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {/* ── Recent Runs ── */}
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        {/* Section header */}
        <Box sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Timer sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>Recent Runs</Typography>
            <Chip
              label={
                history?.totalRuns && history.totalRuns > detailedRuns.length
                  ? `${detailedRuns.length} of ${history.totalRuns}`
                  : detailedRuns.length
              }
              size="small"
              sx={{ height: 20, fontSize: '0.65rem' }}
            />
          </Box>
          <Button
            size="small"
            startIcon={<Settings sx={{ fontSize: 16 }} />}
            onClick={() => openTab('sync-behavior')}
            sx={{ fontWeight: 600, fontSize: '0.75rem' }}
          >
            {t('integrations.detailTabs.syncBehavior')}
          </Button>
        </Box>

        {detailedRuns.length > 0 ? (
          <>
            {/* Column header (desktop/tablet only — mobile uses stacked cards below) */}
            <Box sx={{
              display: { xs: 'none', md: 'grid' },
              gridTemplateColumns: { xs: 'minmax(0,1fr)', md: '190px 100px 80px 100px 75px 80px 75px 80px 64px 40px' },
              gap: 1, px: 2.5, py: 1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider',
            }}>
              {(['Date', 'Status', 'Duration', 'Products', 'Created'] as const).map((h) => (
                <Typography key={h} variant="overline" sx={{ color: 'text.secondary' }}>
                  {h}
                </Typography>
              ))}
              <Tooltip title="What Motzu wrote for products counted as Updated — prices only, changed catalog fields, or a full rewrite.">
                <Typography variant="overline" sx={{ color: 'text.secondary', cursor: 'help' }}>
                  Updated
                </Typography>
              </Tooltip>
              <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                Skipped
              </Typography>
              <Tooltip title="Warning and error counts from this run (shown after it finishes or partially completes)">
                <Typography variant="overline" sx={{ color: 'text.secondary', cursor: 'help' }}>
                  Issues
                </Typography>
              </Tooltip>
              <Tooltip title="While queued or running, cancel stops the background sync (not an error).">
                <Typography variant="overline" sx={{ color: 'text.secondary', cursor: 'help', textAlign: 'right' }}>
                  Actions
                </Typography>
              </Tooltip>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', minWidth: 0 }} aria-hidden />
            </Box>

            {/* Rows */}
            {detailedRuns.map((run: any, idx: number) => {
              const runDate = parseFirestoreDate(syncRunDisplayValue(run));
              const runDateLabel = runDate
                ? runDate.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                : '\u2014';
              const cfg = statusCfg(resolveDisplaySyncStatus(run), theme);
              const errorCount = run.errorCount ?? (run.errors?.length ?? 0);
              const warningCount = run.warningCount ?? (run.warnings?.length ?? 0);
              const isExpanded = expandedRun === run.id;
              const triggerInfo = resolveSyncRunTrigger(run);
              const triggerInterval =
                (typeof run.scheduleInterval === 'string' && run.scheduleInterval) ||
                (triggerInfo.trigger === 'scheduled' ? configuredInterval : undefined);
              const triggerLabel = syncTriggerLabel(triggerInfo.trigger, triggerInterval);
              const actorLabel = syncRunStarterLabel(run.startedBy, t);
              const triggerHint = syncTriggerHint(
                triggerInfo.trigger,
                Boolean(run.triggerInferred) || triggerInfo.inferred,
                triggerInterval,
              );
              const triggerColor =
                triggerInfo.trigger === 'scheduled'
                  ? theme.palette.info.main
                  : triggerInfo.trigger === 'auto_new_products'
                    ? theme.palette.secondary.main
                    : theme.palette.text.secondary;
              const runKind = resolveSyncRunKind(run);
              const kindLabel = syncRunKindLabel(runKind);
              const kindChipLabel = syncRunKindChipLabel(runKind);
              const kindHint = syncRunKindHint(runKind);
              const updatedNoun = syncRunUpdatedNoun(runKind);
              const shopifyExtras =
                integration.platform === 'shopify' ? shopifySyncRunExtras(run as Record<string, unknown>) : [];

              return (
                <React.Fragment key={run.id}>
                  {/* Desktop/tablet grid row */}
                  <Box
                    onClick={() => setExpandedRun(isExpanded ? null : run.id)}
                    sx={{
                      display: { xs: 'none', md: 'grid' },
                      gridTemplateColumns: { xs: 'minmax(0,1fr)', md: '190px 100px 80px 100px 75px 80px 75px 80px 64px 40px' },
                      gap: 1, px: 2.5, py: 1.5, alignItems: 'center', cursor: 'pointer',
                      borderBottom: '1px solid', borderColor: isExpanded ? 'transparent' : 'divider',
                      bgcolor: isExpanded ? alpha(theme.palette.info.main, 0.03) : 'transparent',
                      transition: 'background 0.15s',
                      '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.04) },
                      ...(run.status === 'running' && { bgcolor: alpha(theme.palette.primary.main, 0.03) }),
                    }}
                  >
                    {/* Date — completedAt for finished runs so this matches Last Sync */}
                    <Box>
                      <Tooltip
                        title={
                          run.startedAt && run.completedAt && run.status !== 'queued' && run.status !== 'running'
                            ? `Started ${fmtDT(run.startedAt)}`
                            : ''
                        }
                      >
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>{runDateLabel}</Typography>
                      </Tooltip>
                      <Box sx={{ color: triggerColor }}>
                        <SyncRunTriggerBlock
                          triggerLabel={triggerLabel}
                          actorLabel={actorLabel}
                          hint={triggerHint}
                          scheduled={triggerInfo.trigger === 'scheduled'}
                          size="row"
                        />
                      </Box>
                      <Tooltip title={kindHint}>
                        <Box sx={{ mt: 0.5 }}>
                          <StatusChip label={kindChipLabel} tone={syncRunKindTone(runKind)} size="small" />
                        </Box>
                      </Tooltip>
                    </Box>

                    {/* Status pill */}
                    <Box>
                      <Box sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.3,
                        borderRadius: 1, bgcolor: alpha(cfg.color, 0.1), color: cfg.color,
                        fontWeight: 600, fontSize: '0.7rem', lineHeight: 1,
                      }}>
                        {cfg.icon}
                        {cfg.label}{wroteNothing(run) ? ' · nothing written' : ''}
                      </Box>
                    </Box>

                    {/* Duration */}
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                      {(run.status === 'running' || run.status === 'queued')
                        ? fmtDuration(getElapsedMs(run, now))
                        : fmtDuration(run.duration || 0)}
                    </Typography>

                    {/* Products */}
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                      {run.productsTotal
                        ? `${Math.min((run.productsProcessed ?? 0) + (run.productsSkipped ?? 0), run.productsTotal)} / ${run.productsTotal}`
                        : (run.productsProcessed ?? 0)}
                    </Typography>

                    {/* Created */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TrendingUp sx={{ fontSize: 14, color: 'success.main' }} />
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{run.productsCreated ?? 0}</Typography>
                    </Box>

                    {/* Updated */}
                    <Tooltip title={kindHint}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Refresh sx={{ fontSize: 14, color: 'info.main' }} />
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{run.productsUpdated ?? 0}</Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                          {updatedNoun}
                        </Typography>
                      </Box>
                    </Tooltip>

                    {/* Skipped */}
                    <Tooltip title={t('integrations.syncRun.columnHint')}>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                        {run.productsSkipped ?? 0}
                      </Typography>
                    </Tooltip>

                    {/* Issues (not the cancel button — that lives under Actions) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 28 }}>
                      {warningCount > 0 && (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, px: 0.75, py: 0.2, borderRadius: 0.75, bgcolor: alpha(theme.palette.warning.main, 0.1), color: 'warning.main', fontSize: '0.7rem', fontWeight: 600 }}>
                          <Warning sx={{ fontSize: 12 }} />{warningCount}
                        </Box>
                      )}
                      {errorCount > 0 && (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, px: 0.75, py: 0.2, borderRadius: 0.75, bgcolor: alpha(theme.palette.error.main, 0.1), color: 'error.main', fontSize: '0.7rem', fontWeight: 600 }}>
                          <ErrorIcon sx={{ fontSize: 12 }} />{errorCount}
                        </Box>
                      )}
                      {warningCount === 0 && errorCount === 0 && (
                        <Typography variant="caption" color="text.disabled">None</Typography>
                      )}
                    </Box>

                    {/* Actions: cancel only */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25 }}>
                      {(run.status === 'queued' || run.status === 'running') && (
                        <Tooltip title="Cancel this sync (not an error — stops the background job)">
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); cancelSyncMutation.mutate(run.id); }}
                            disabled={cancelSyncMutation.isPending}
                            sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.08) } }}
                            aria-label="Cancel sync"
                          >
                            <Close sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>

                    {/* Expand chevron — rightmost column (outside Actions) */}
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pr: 0.25 }}
                      onClick={(e) => { e.stopPropagation(); setExpandedRun(isExpanded ? null : run.id); }}
                      aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                    >
                      <ExpandMore sx={{ fontSize: 18, color: 'text.secondary', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'none' }} />
                    </Box>
                  </Box>

                  {/* Mobile stacked card row */}
                  <Box
                    onClick={() => setExpandedRun(isExpanded ? null : run.id)}
                    sx={{
                      display: { xs: 'flex', md: 'none' },
                      flexDirection: 'column', gap: 0.75, px: 2, py: 1.5, cursor: 'pointer',
                      borderBottom: '1px solid', borderColor: isExpanded ? 'transparent' : 'divider',
                      bgcolor: isExpanded ? alpha(theme.palette.info.main, 0.03) : 'transparent',
                      ...(run.status === 'running' && { bgcolor: alpha(theme.palette.primary.main, 0.03) }),
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{runDateLabel}</Typography>
                        <Box sx={{ color: triggerColor }}>
                          <SyncRunTriggerBlock
                            triggerLabel={triggerLabel}
                            actorLabel={actorLabel}
                            hint={triggerHint}
                            scheduled={triggerInfo.trigger === 'scheduled'}
                            size="row"
                          />
                        </Box>
                        <Tooltip title={kindHint}>
                          <Box sx={{ mt: 0.5 }}>
                            <StatusChip label={kindChipLabel} tone={syncRunKindTone(runKind)} size="small" />
                          </Box>
                        </Tooltip>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                        <Box sx={{
                          display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.3,
                          borderRadius: 1, bgcolor: alpha(cfg.color, 0.1), color: cfg.color,
                          fontWeight: 600, fontSize: '0.7rem', lineHeight: 1,
                        }}>
                          {cfg.icon}
                          {cfg.label}{wroteNothing(run) ? ' · nothing written' : ''}
                        </Box>
                        <ExpandMore sx={{ fontSize: 18, color: 'text.secondary', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'none', flexShrink: 0 }} />
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {(run.status === 'running' || run.status === 'queued')
                          ? fmtDuration(getElapsedMs(run, now))
                          : fmtDuration(run.duration || 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {run.productsTotal
                          ? `${Math.min((run.productsProcessed ?? 0) + (run.productsSkipped ?? 0), run.productsTotal)} / ${run.productsTotal} products`
                          : `${run.productsProcessed ?? 0} products`}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                        <TrendingUp sx={{ fontSize: 13, color: 'success.main' }} />
                        <Typography variant="caption">{run.productsCreated ?? 0}</Typography>
                      </Box>
                      <Tooltip title={kindHint}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <Refresh sx={{ fontSize: 13, color: 'info.main' }} />
                          <Typography variant="caption">{run.productsUpdated ?? 0} {updatedNoun}</Typography>
                        </Box>
                      </Tooltip>
                      {warningCount > 0 && (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, px: 0.75, py: 0.2, borderRadius: 0.75, bgcolor: alpha(theme.palette.warning.main, 0.1), color: 'warning.main', fontSize: '0.7rem', fontWeight: 600 }}>
                          <Warning sx={{ fontSize: 12 }} />{warningCount}
                        </Box>
                      )}
                      {errorCount > 0 && (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, px: 0.75, py: 0.2, borderRadius: 0.75, bgcolor: alpha(theme.palette.error.main, 0.1), color: 'error.main', fontSize: '0.7rem', fontWeight: 600 }}>
                          <ErrorIcon sx={{ fontSize: 12 }} />{errorCount}
                        </Box>
                      )}
                      {(run.status === 'queued' || run.status === 'running') && (
                        <Button
                          size="small"
                          color="error"
                          onClick={(e) => { e.stopPropagation(); cancelSyncMutation.mutate(run.id); }}
                          disabled={cancelSyncMutation.isPending}
                          sx={{ ml: 'auto', minWidth: 0, py: 0, px: 0.75, fontSize: '0.7rem' }}
                        >
                          Cancel
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {/* Expanded details */}
                  <Collapse in={isExpanded} unmountOnExit>
                    <Box sx={{ px: 2.5, py: 2, bgcolor: alpha(theme.palette.info.main, 0.02), borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', gap: 4, mb: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                        <RunDetailField label="Trigger">
                          <Box sx={{ color: triggerColor }}>
                            <SyncRunTriggerBlock
                              triggerLabel={triggerLabel}
                              actorLabel={actorLabel}
                              hint={triggerHint}
                              scheduled={triggerInfo.trigger === 'scheduled'}
                              size="detail"
                            />
                          </Box>
                        </RunDetailField>
                        <RunDetailField label="Run ID">
                          <Typography variant="caption" component="div" sx={{ fontFamily: tokens.font.mono, color: 'text.secondary' }}>{run.id}</Typography>
                        </RunDetailField>
                        <RunDetailField label="Started">
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{fmtDT(run.startedAt || run.queuedAt || run.createdAt)}</Typography>
                        </RunDetailField>
                        {run.completedAt && (
                          <RunDetailField label="Completed">
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{fmtDT(run.completedAt)}</Typography>
                          </RunDetailField>
                        )}
                        <RunDetailField label="Duration">
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {(run.status === 'running' || run.status === 'queued')
                              ? fmtDuration(getElapsedMs(run, now))
                              : fmtDuration(run.duration || 0)}
                          </Typography>
                        </RunDetailField>
                        {run.productsTotal > 0 && (
                          <RunDetailField label="Progress">
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                              {Math.min((run.productsProcessed ?? 0) + (run.productsSkipped ?? 0), run.productsTotal)} of {run.productsTotal} products
                              {run.productsTotal > 0 && ` (${Math.min(Math.round(((Math.min((run.productsProcessed ?? 0) + (run.productsSkipped ?? 0), run.productsTotal)) / run.productsTotal) * 100), 100)}%)`}
                            </Typography>
                          </RunDetailField>
                        )}
                        {(() => {
                          const rowEta = run.status === 'running' ? estimateRemainingMs(run, now) : null;
                          if (rowEta === null) return null;
                          return (
                            <RunDetailField label="Est. remaining">
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                                ~{fmtEtaRemaining(rowEta)}
                              </Typography>
                            </RunDetailField>
                          );
                        })()}
                      </Box>

                      <Box sx={{ mb: 2, maxWidth: 720 }}>
                        <Typography variant="overline" color="text.disabled" sx={{ display: 'block', mb: 0.5 }}>Sync kind</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                          <StatusChip label={kindLabel} tone={syncRunKindTone(runKind)} size="small" />
                          {(run.productsUpdated ?? 0) > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              Updated {run.productsUpdated} {updatedNoun}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.5 }}>
                          {kindHint}
                          {shopifyExtras.length > 0 ? ` ${shopifyExtras.join(' · ')}` : ''}
                        </Typography>
                      </Box>

                      {getSyncZeroProgressExplanation(run, now) && (
                        <Alert
                          severity="info"
                          icon={<InfoOutlined fontSize="inherit" />}
                          sx={{ mb: 2, py: 1, alignItems: 'flex-start', '& .MuiAlert-message': { width: '100%' } }}
                        >
                          <Typography variant="body2" sx={{ fontSize: '0.82rem', lineHeight: 1.5 }}>
                            {getSyncZeroProgressExplanation(run, now)}
                          </Typography>
                        </Alert>
                      )}

                      {((run.warningCount ?? 0) > 0 || (run.warnings?.length ?? 0) > 0) && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="overline" sx={{ color: 'warning.main', mb: 0.5, display: 'block' }}>
                            Warnings ({run.warningCount ?? run.warnings?.length ?? 0})
                          </Typography>
                          {(() => {
                            type StructuredWarning = { productId: string; message: string; code?: string; productName?: string; productSku?: string };
                            const raw: unknown[] = run.warnings || [];
                            const structured: StructuredWarning[] = raw.filter(
                              (w: unknown): w is StructuredWarning =>
                                typeof w === 'object' && w !== null && 'message' in w && typeof (w as { message: unknown }).message === 'string',
                            );
                            const legacyStrings = raw.filter((w: unknown): w is string => typeof w === 'string');
                            const legacyDeduped =
                              legacyStrings.length > 0 &&
                              structured.length === 0 &&
                              (run.warningCount ?? 0) > legacyStrings.length;

                            return (
                              <>
                                {structured.length > 0 && (
                                  <Paper variant="outlined" sx={{ overflow: 'hidden', mb: legacyStrings.length > 0 ? 1 : 0 }}>
                                    <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(120px, 0.35fr) 1fr' }, bgcolor: alpha(theme.palette.warning.main, 0.08), px: 1.5, py: 0.75 }}>
                                      <Typography variant="overline" sx={{ color: 'text.secondary' }}>Product</Typography>
                                      <Typography variant="overline" sx={{ color: 'text.secondary' }}>Warning</Typography>
                                    </Box>
                                    {structured.map((w, i: number) => (
                                      <Box
                                        key={`${w.productId}-${i}`}
                                        sx={{
                                          display: 'grid',
                                          gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(120px, 0.35fr) 1fr' },
                                          gap: { xs: 0.5, md: 0 },
                                          px: 1.5,
                                          py: 0.75,
                                          borderTop: '1px solid',
                                          borderColor: 'divider',
                                          '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.03) },
                                        }}
                                      >
                                        <Box sx={{ minWidth: 0 }}>
                                          {w.productName ? (
                                            <>
                                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, wordBreak: 'break-all' }}>
                                                {w.productName}
                                              </Typography>
                                              {w.productId ? (
                                                <Typography variant="caption" sx={{ display: 'block', fontFamily: tokens.font.mono, color: 'text.disabled', wordBreak: 'break-all' }}>
                                                  {w.productId}
                                                </Typography>
                                              ) : null}
                                              {w.productSku ? (
                                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>{w.productSku}</Typography>
                                              ) : null}
                                            </>
                                          ) : (
                                            <Typography variant="caption" sx={{ fontFamily: tokens.font.mono, color: 'text.secondary', wordBreak: 'break-all' }}>
                                              {w.productId || '—'}
                                            </Typography>
                                          )}
                                        </Box>
                                        <Box sx={{ minWidth: 0 }}>
                                          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'warning.dark' }}>
                                            {w.message}
                                          </Typography>
                                          {w.code ? (
                                            <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', mt: 0.25 }}>
                                              {w.code}
                                            </Typography>
                                          ) : null}
                                          {w.productId && (
                                            <Box sx={{ mt: 0.75 }}>
                                              {w.code === 'suspicious_discount' && (
                                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
                                                  Held for Motzu admin review of stored catalog pricing.
                                                </Typography>
                                              )}
                                              {w.code === 'no_valid_price' && (
                                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
                                                  {isAdmin
                                                    ? 'Flagged for Motzu review — add manufacturer pricing, or the store can set an override.'
                                                    : 'Flagged for Motzu review. Open the product to set a store price override if you still want it listed.'}
                                                </Typography>
                                              )}
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                                {(w.code === 'suspicious_discount' || w.code === 'below_map' || w.code === 'no_valid_price' || w.code === 'shopify_unmatched_skipped') && (
                                                  <Button
                                                    size="small"
                                                    variant={isAdmin ? 'contained' : 'text'}
                                                    onClick={() =>
                                                      navigate(
                                                        `/app/products?productId=${encodeURIComponent(w.productId)}`
                                                      )
                                                    }
                                                    sx={{ minWidth: 0, px: isAdmin ? 1 : 0.5, fontSize: '0.75rem' }}
                                                  >
                                                    {w.code === 'suspicious_discount' && isAdmin ? 'Review stored pricing' : 'View product'}
                                                  </Button>
                                                )}
                                                <ReportSyncIssueButton
                                                  issue={{
                                                    productId: w.productId,
                                                    message: w.message,
                                                    code: w.code,
                                                    syncRunId: run.id,
                                                    integrationId: id,
                                                    integrationName: integration.name,
                                                  }}
                                                  reported={isIssueReported(w)}
                                                  onReported={handleIssueReported}
                                                  onError={(message) => showToast(message, 'error')}
                                                />
                                              </Box>
                                            </Box>
                                          )}
                                        </Box>
                                      </Box>
                                    ))}
                                  </Paper>
                                )}
                                {legacyStrings.map((w: string, i: number) => (
                                  <Alert key={`lw-${i}`} severity="warning" sx={{ mb: 0.5, py: 0.25, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>{w}</Alert>
                                ))}
                                {legacyDeduped && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.4 }}>
                                    Older runs stored identical messages once in Firestore. Total warning events for this run: {run.warningCount}.
                                  </Typography>
                                )}
                              </>
                            );
                          })()}
                        </Box>
                      )}

                      {(run.errors?.length > 0 || (run.errorCount ?? 0) > 0) && (
                        <Box>
                          {(() => {
                            const structured = run.errors.filter((e: any) => typeof e === 'object' && e.productId);
                            const legacy = run.errors.filter((e: any) => typeof e === 'string') as string[];
                            const runLevel = legacy.filter((e) => /marked stale|closed as stale/i.test(e));
                            // A run that stopped on a problem only the store can fix (MOT-796, a read-only
                            // API key) carries its one message in `errors` and a `runErrorCode`; it is not a product failure.
                            const stoppedMessages = typeof run.runErrorCode === 'string' && run.runErrorCode && typeof run.runErrorMessage === 'string'
                              ? legacy.filter((e) => e === run.runErrorMessage)
                              : [];
                            const otherLegacy = legacy.filter((e) => !/marked stale|closed as stale/i.test(e) && !stoppedMessages.includes(e));
                            const productFailureCount = structured.length + otherLegacy.length;
                            return (
                              <>
                                {stoppedMessages.length > 0 && (
                                  <Box sx={{ mb: productFailureCount > 0 ? 1.5 : 0 }}>
                                    <Typography variant="overline" sx={{ color: 'error.main', mb: 1, display: 'block' }}>
                                      {t('integrations.syncRun.runFailure.title')}
                                    </Typography>
                                    {stoppedMessages.map((e: string, i: number) => (
                                      <Alert
                                        key={`stopped-${i}`}
                                        severity="error"
                                        sx={{ mb: 0.5, py: 0.25, '& .MuiAlert-message': { fontSize: '0.8rem', overflowWrap: 'anywhere' } }}
                                        action={
                                          <Button color="inherit" size="small" onClick={() => openTab('credentials')}>
                                            {t('integrations.syncRun.runFailure.openCredentials')}
                                          </Button>
                                        }
                                      >
                                        {e}
                                      </Alert>
                                    ))}
                                  </Box>
                                )}
                                {runLevel.length > 0 && (
                                  <Box sx={{ mb: productFailureCount > 0 ? 1.5 : 0 }}>
                                    <Typography variant="overline" sx={{ color: 'warning.dark', mb: 1, display: 'block' }}>
                                      Run closed early
                                    </Typography>
                                    {runLevel.map((e: string, i: number) => (
                                      <Alert key={`stale-${i}`} severity="warning" sx={{ mb: 0.5, py: 0.25, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
                                        {e}
                                      </Alert>
                                    ))}
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.45 }}>
                                      This is a queue timeout, not a product failure. Large catalogs can take longer than 30 minutes; if syncs keep restarting, wait for one run to finish before starting another.
                                    </Typography>
                                  </Box>
                                )}
                                {productFailureCount > 0 && (
                                  <Typography variant="overline" sx={{ color: 'error.main', mb: 1, display: 'block' }}>
                                    Failed Products ({productFailureCount})
                                  </Typography>
                                )}
                                {structured.length > 0 && (
                                  <Paper variant="outlined" sx={{ overflow: 'hidden', mb: otherLegacy.length > 0 ? 1.5 : 0 }}>
                                    <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: { xs: 'minmax(0,1fr)', md: '1fr 100px minmax(200px, 2fr)' }, bgcolor: alpha(theme.palette.error.main, 0.06), px: 1.5, py: 0.75 }}>
                                      <Typography variant="overline" sx={{ color: 'text.secondary' }}>Product</Typography>
                                      <Typography variant="overline" sx={{ color: 'text.secondary' }}>SKU</Typography>
                                      <Typography variant="overline" sx={{ color: 'text.secondary' }}>Error</Typography>
                                    </Box>
                                    {structured.map((e: any, i: number) => {
                                      const failure = presentSyncFailure(String(e.error || ''));
                                      const friendlyError = failure.kind === 'other'
                                        ? formatSyncError(String(e.error || ''))
                                        : t(`integrations.syncRun.failures.${failure.kind}.title`, { platform: platformLabel });
                                      return (
                                        <Box key={i} sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: '1fr 100px minmax(200px, 2fr)' }, gap: { xs: 0.5, md: 0 }, px: 1.5, py: 0.75, borderTop: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.02) } }}>
                                          <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>{e.productName || e.productId}</Typography>
                                            {e.productName && <Typography variant="caption" sx={{ color: 'text.disabled' }}>{e.productId}</Typography>}
                                          </Box>
                                          <Box sx={{ minWidth: 0 }}>
                                            <Typography component="span" sx={{ display: { xs: 'block', md: 'none' }, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: tokens.ink.faint, mb: '2px' }}>
                                              SKU
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', fontFamily: tokens.font.mono }}>{e.productSku || '—'}</Typography>
                                          </Box>
                                          <Box sx={{ minWidth: 0 }}>
                                            <Typography component="span" sx={{ display: { xs: 'block', md: 'none' }, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: tokens.ink.faint, mb: '2px' }}>
                                              Error
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'error.main', overflowWrap: 'anywhere' }}>{friendlyError}</Typography>
                                            {failure.kind !== 'other' && (
                                              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25, overflowWrap: 'anywhere' }}>
                                                {t(`integrations.syncRun.failures.${failure.kind}.detail`, { platform: platformLabel })}
                                              </Typography>
                                            )}
                                            <SyncErrorDetail requestBodyPreview={e.requestBodyPreview} providerMessage={e.providerMessage} truncated={e.requestBodyPreviewTruncated === true} />
                                            {failure.reportable && (
                                            <Box sx={{ mt: 0.5 }}>
                                              <ReportSyncIssueButton
                                                issue={{
                                                  productId: e.productId,
                                                  productSku: e.productSku,
                                                  productName: e.productName,
                                                  message: friendlyError,
                                                  fallbackCode: 'sync_error',
                                                  syncRunId: run.id,
                                                  integrationId: id,
                                                  integrationName: integration.name,
                                                }}
                                                reported={isIssueReported({ productId: e.productId, message: friendlyError })}
                                                onReported={handleIssueReported}
                                                onError={(message) => showToast(message, 'error')}
                                              />
                                            </Box>
                                            )}
                                          </Box>
                                        </Box>
                                      );
                                    })}
                                  </Paper>
                                )}
                                {otherLegacy.map((e: string, i: number) => (
                                  <Alert key={`legacy-${i}`} severity="error" sx={{ mb: 0.5, py: 0.25, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>{e}</Alert>
                                ))}
                                {integration.platform === 'shopify' &&
                                  Array.isArray(run.errors) &&
                                  run.errors.some((e: unknown) => {
                                    const msg =
                                      typeof e === 'string'
                                        ? e
                                        : typeof e === 'object' && e !== null && 'error' in e
                                          ? String((e as { error?: unknown }).error ?? '')
                                          : '';
                                    return msg.includes('product_tags') || /\btags\b.*invalid/i.test(msg);
                                  }) && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25, lineHeight: 1.45 }}>
                                      Failed products are attempted again on the next sync. After a fix is deployed, use{' '}
                                      <strong>Sync now</strong> to retry, or <strong>Force full sync</strong> from the Sync menu if those
                                      products were skipped as &quot;unchanged&quot;.
                                    </Typography>
                                  )}
                              </>
                            );
                          })()}
                        </Box>
                      )}

                      {(run.productsSkipped > 0) && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="overline" sx={{ color: 'grey.500', mb: 0.5, display: 'block' }}>
                            Skipped ({run.productsSkipped})
                          </Typography>
                          {run.skipReasons && typeof run.skipReasons === 'object' ? (
                            <Box>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 1, overflowWrap: 'anywhere' }}>
                                {t('integrations.syncRun.intro')}
                              </Typography>
                              {(() => {
                                const grouped = groupSkipReasons(run.skipReasons as Record<string, number>, integration.platform);
                                const skippedCap = Math.max(0, Math.floor(Number(run.productsSkipped) || 0));
                                const notSentRows = clampSkipCounts(
                                  grouped.filter((row) => row.presentation.group === 'notSent'),
                                  skippedCap,
                                );
                                const unchangedCap = Math.max(0, skippedCap - notSentRows.reduce((sum, row) => sum + row.count, 0));
                                const rowsByGroup = {
                                  notSent: notSentRows,
                                  unchanged: clampSkipCounts(
                                    grouped.filter((row) => row.presentation.group === 'unchanged'),
                                    unchangedCap,
                                  ),
                                };
                                return (['notSent', 'unchanged'] as const).map((group) => {
                                const rows = rowsByGroup[group];
                                if (rows.length === 0) return null;
                                const total = rows.reduce((sum, row) => sum + row.count, 0);
                                return (
                                  <Box key={group} sx={{ mb: 1.5 }}>
                                    <Typography variant="overline" sx={{ color: group === 'notSent' ? 'warning.dark' : 'text.secondary', display: 'block' }}>
                                      {t(`integrations.syncRun.${group}Heading`, { count: total })}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.75, overflowWrap: 'anywhere' }}>
                                      {t(`integrations.syncRun.${group}Note`)}
                                    </Typography>
                                    <Paper variant="outlined">
                                      {rows.map((row) => {
                                        const { presentation } = row;
                                        return (
                                          <Box
                                            key={presentation.kind === 'other' ? row.notes[0] : presentation.kind}
                                            sx={{
                                              display: 'grid',
                                              gridTemplateColumns: { xs: 'minmax(0,1fr) 64px', md: '1fr 80px' },
                                              gap: 1,
                                              px: 1.5,
                                              py: 1,
                                              borderTop: '1px solid',
                                              borderColor: 'divider',
                                              '&:first-of-type': { borderTop: 'none' },
                                            }}
                                          >
                                            <Box sx={{ minWidth: 0 }}>
                                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, overflowWrap: 'anywhere' }}>
                                                {t(`integrations.syncRun.kinds.${presentation.kind}.title`)}
                                              </Typography>
                                              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', overflowWrap: 'anywhere' }}>
                                                {t(`integrations.syncRun.kinds.${presentation.kind}.detail`)}
                                              </Typography>
                                              {row.notes.map((note) => (
                                                <Typography key={note} variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25, overflowWrap: 'anywhere' }}>
                                                  {note}
                                                </Typography>
                                              ))}
                                              {presentation.truncated && (
                                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25, overflowWrap: 'anywhere' }}>
                                                  {t('integrations.syncRun.truncatedNote')}
                                                </Typography>
                                              )}
                                              {(presentation.settings || (presentation.reviewListings && id)) && (
                                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                                {presentation.settings && (
                                                  <Link
                                                    component="button"
                                                    type="button"
                                                    variant="body2"
                                                    onClick={() => openSettingsAt(presentation.settings!.anchor)}
                                                    sx={{ fontSize: '0.8rem', fontWeight: 600 }}
                                                  >
                                                    {t('integrations.syncRun.settingsLink', { label: presentation.settings.label })}
                                                  </Link>
                                                )}
                                                {presentation.reviewListings && id && (
                                                  <Link
                                                    component="button"
                                                    type="button"
                                                    variant="body2"
                                                    onClick={() => navigate(shopifyMappingHref(id, 'listing-matches'))}
                                                    sx={{ fontSize: '0.8rem', fontWeight: 600 }}
                                                  >
                                                    {t('integrations.syncRun.reviewListings')}
                                                  </Link>
                                                )}
                                              </Box>
                                              )}
                                            </Box>
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                              {row.count}
                                            </Typography>
                                          </Box>
                                        );
                                      })}
                                    </Paper>
                                  </Box>
                                );
                              });
                              })()}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                              {run.productsSkipped} product{run.productsSkipped !== 1 ? 's' : ''} skipped. This run predates skip-reason tracking, so no breakdown was recorded — the next sync will show reasons here (e.g. already up to date, price held below MAP, excluded by settings).
                            </Typography>
                          )}
                        </Box>
                      )}

                      {!(run.warningCount ?? 0) && !(run.warnings?.length ?? 0) && !(run.errorCount ?? 0) && !(run.errors?.length ?? 0) && !(run.productsSkipped > 0) && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>No warnings or errors for this run.</Typography>
                      )}

                      {/* Flag `shopify_price_ledger` (MOT-500): every variant price this run wrote or refused. */}
                      {priceLedgerEnabled && integration.platform === 'shopify' && id && run.status !== 'queued' && (
                        <Box sx={{ mt: 2 }}>
                          <ShopifyPriceLedgerSection integrationId={id} runId={run.id} />
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </React.Fragment>
              );
            })}
          </>
        ) : (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Timer sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>No sync runs yet</Typography>
            <Typography variant="caption" color="text.disabled">Click "Sync Now" to run your first sync.</Typography>
          </Box>
        )}
      </Paper>
      </>)}

    </PageContainer>
  );
};

export default IntegrationDetailPage;
